import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react"
import { View } from "react-native"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { connect } from "react-redux"
import TextInputM from "@common/components/input/TextInputM"
import TextM from "@common/components/text/TextM"
import { AddressFormFields } from "@common/components/address/AddressFormFields"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { countryOptions, addressSchema } from "@data/constants/formConstants"

function getDefaultValues(address, user) {
    return {
        parent_firstname: user?.parent_firstname ?? "",
        parent_lastname: user?.parent_lastname ?? "",
        street: address?.street ?? "",
        complement: address?.complement ?? "",
        city: address?.city ?? "",
        postal_code: address?.postal_code ?? "",
        country: address?.country
            ? (countryOptions.find((c) => c.value === address.country) ?? {
                  value: "FR",
                  label: "France",
              })
            : { value: "FR", label: "France" },
    }
}

function NewSubscriptionAddressCardComponent(props, ref) {
    const { address, user, userEmail, containerStyle } = props
    const saveResolveRef = useRef(null)
    const lastNameRef = useRef(null)
    const streetRef = useRef(null)
    const complementRef = useRef(null)
    const postalCodeRef = useRef(null)
    const cityRef = useRef(null)

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: getDefaultValues(address, user),
        resolver: yupResolver(addressSchema),
    })

    useApiStatus(
        authService.updateBillingAddress,
        "",
        true,
        () => {
            if (saveResolveRef.current) {
                saveResolveRef.current(true)
                saveResolveRef.current = null
            }
        },
        () => {
            if (saveResolveRef.current) {
                saveResolveRef.current(false)
                saveResolveRef.current = null
            }
        }
    )

    const doSave = useCallback(
        (data) => {
            props.apiRequest(
                authService.updateBillingAddress,
                {},
                {
                    parent_firstname: data.parent_firstname,
                    parent_lastname: data.parent_lastname,
                    street: data.street,
                    complement: data.complement,
                    city: data.city,
                    postal_code: data.postal_code,
                    country: data.country?.value ?? "FR",
                },
                userEmail ? { email: userEmail } : undefined
            )
        },
        [props.apiRequest, userEmail]
    )

    // Resolve pending promise on unmount to avoid hanging
    useEffect(() => {
        return () => {
            if (saveResolveRef.current) {
                saveResolveRef.current(false)
                saveResolveRef.current = null
            }
        }
    }, [])

    // Expose save() — parent calls this before payment
    useImperativeHandle(
        ref,
        () => ({
            save: () =>
                new Promise((resolve) => {
                    saveResolveRef.current = resolve
                    handleSubmit(
                        (data) => doSave(data),
                        () => {
                            saveResolveRef.current = null
                            resolve(false)
                        }
                    )()
                }),
        }),
        [handleSubmit, doSave]
    )

    return (
        <View
            style={{
                padding: 20,
                borderRadius: 20,
                gap: 12,
                borderWidth: 1,
                borderColor: theme.colors.lightMore,
                ...containerStyle,
            }}
        >
            <TextM fontWeight="bold" fontSize={18} color={theme.colors.black}>
                Adresse de facturation
            </TextM>

            <TextInputM
                contentContainerStyle={{ marginBottom: 8 }}
                placeholder="Prénom du parent"
                name="parent_firstname"
                labelTop="Prénom du parent"
                control={control}
                errors={errors}
                width="100%"
                returnKeyType="next"
                onSubmitEditing={() => lastNameRef.current?.focus()}
                blurOnSubmit={false}
            />

            <TextInputM
                ref={lastNameRef}
                contentContainerStyle={{ marginBottom: 8 }}
                placeholder="Nom du parent"
                name="parent_lastname"
                labelTop="Nom du parent"
                control={control}
                errors={errors}
                width="100%"
                returnKeyType="next"
                onSubmitEditing={() => streetRef.current?.focus()}
                blurOnSubmit={false}
            />

            <AddressFormFields
                control={control}
                errors={errors}
                spacing={8}
                selectBoxWidth="100%"
                inputWidth="100%"
                streetRef={streetRef}
                complementRef={complementRef}
                postalCodeRef={postalCodeRef}
                cityRef={cityRef}
            />
        </View>
    )
}

const ForwardedComponent = forwardRef(NewSubscriptionAddressCardComponent)

const mapStateToProps = (state, ownProps) => {
    const fallbackUser = state.data.currentUser?.object
    return {
        user: ownProps.user ?? fallbackUser,
        address: ownProps.user ? ownProps.address : fallbackUser?.address,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export const NewSubscriptionAddressCard = connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { forwardRef: true }
)(ForwardedComponent)
